import React, { useState } from 'react';
import { 
  Info, 
  GraduationCap, 
  BookOpen, 
  Award, 
  Briefcase, 
  Users, 
  CreditCard, 
  Lock, 
  HelpCircle, 
  Building2, 
  Mail,
  ChevronDown,
  Search
} from 'lucide-react';
import Layout from '../component/layout/Layout';

export default function FAQPage() {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [activeCategory, setActiveCategory] = useState('all');
  const [openQuestion, setOpenQuestion] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  React.useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const categories = [
    { id: 'all', name: 'All Questions', icon: HelpCircle },
    { id: 'about', name: 'About Skill Serve', icon: Info },
    { id: 'enrollment', name: 'Course Enrollment', icon: GraduationCap },
    { id: 'content', name: 'Course Content', icon: BookOpen },
    { id: 'certification', name: 'Certification', icon: Award },
    { id: 'placement', name: 'Placement Support', icon: Briefcase },
    { id: 'internship', name: 'Internship', icon: Users },
    { id: 'payment', name: 'Fees & Payments', icon: CreditCard },
    { id: 'lms', name: 'LMS Access', icon: Lock },
    { id: 'support', name: 'Technical Support', icon: HelpCircle },
    { id: 'corporate', name: 'Corporate Training', icon: Building2 },
    { id: 'contact', name: 'Contact & Support', icon: Mail },
  ];

  const faqData = [
    {
      category: 'about',
      question: 'What is Skill Serve?',
      answer: 'Skill Serve is an online skill development and e-learning platform offering job-ready courses, industry-focused training, soft skills, and customized corporate programs.'
    },
    {
      category: 'about',
      question: 'Who can use Skill Serve?',
      answer: 'Students, job seekers, working professionals, trainers, and corporate teams. Our courses support all learning levels.'
    },
    {
      category: 'enrollment',
      question: 'How do I enroll in a course?',
      answer: 'Go to www.skillserve.in → Choose Course → Enroll Now/Buy Course → Register → Make Payment (if required). Access starts instantly through the SkillServe LMS.'
    },
    {
      category: 'enrollment',
      question: 'Do I need prior knowledge?',
      answer: 'No. Most courses start from basics. Any prerequisites are mentioned on the course page.'
    },
    {
      category: 'enrollment',
      question: 'Can I join multiple courses?',
      answer: 'Yes, you can enroll in as many courses as you want. All will appear on your LMS dashboard.'
    },
    {
      category: 'content',
      question: 'What content is included in Skill Serve courses?',
      answer: 'Video lessons, Content (PPT & PDF), worksheets, quizzes, and assignments.'
    },
    {
      category: 'content',
      question: 'Are the courses self-paced?',
      answer: 'Yes. You can learn anytime from anywhere. Your progress is saved automatically.'
    },
    {
      category: 'content',
      question: 'How long can I access the course?',
      answer: 'Access is usually valid for 6–12 months, depending on the course.'
    },
    {
      category: 'certification',
      question: 'Will I get a certificate after completing a course?',
      answer: 'Yes. You will receive a Skill Serve Certificate after completing all modules and assessments.'
    },
    {
      category: 'placement',
      question: 'Does Skill Serve provide placement support?',
      answer: 'Yes. We help with resume building, interview preparation, mock interviews, job alerts, and connecting you with hiring partners.'
    },
    {
      category: 'internship',
      question: 'Does Skill Serve offer internship opportunities?',
      answer: 'Yes. Selected courses come with internship support through industry partners.'
    },
    {
      category: 'internship',
      question: 'How do I apply for internships or apprenticeships?',
      answer: '1. Complete your course\n2. Update your LMS profile\n3. Submit the internship request form\n4. Our placement team will contact you if you meet the criteria'
    },
    {
      category: 'internship',
      question: 'Are internships or apprenticeships guaranteed?',
      answer: 'Opportunities depend on your performance and company requirements. Skill Serve helps you get shortlisted but cannot guarantee selection.'
    },
    {
      category: 'payment',
      question: 'Are the courses free or paid?',
      answer: 'We offer both free and paid online courses. Pricing is listed on every course page.'
    },
    {
      category: 'payment',
      question: 'What payment options are available?',
      answer: 'UPI, debit/credit cards, net banking, and secure payment gateways.'
    },
    {
      category: 'payment',
      question: 'Do you offer refunds?',
      answer: 'Refunds depend on the nature of the course. Check our Refund Policy or contact support.'
    },
    {
      category: 'lms',
      question: 'How do I log in to the Skill Serve LMS?',
      answer: 'Visit www.skillserve.in → Login → Enter your email & password. Use Forgot Password if you need to reset.'
    },
    {
      category: 'lms',
      question: 'Can I access the LMS on mobile?',
      answer: 'Yes. Skill Serve works smoothly on mobile browsers, tablets, laptops, and desktops.'
    },
    {
      category: 'lms',
      question: 'Do you have a mobile app?',
      answer: 'You can use our mobile-friendly web LMS. A dedicated Android/iOS app will be launched soon.'
    },
    {
      category: 'support',
      question: 'What should I do if videos are not playing?',
      answer: 'Try these steps:\n• Check your internet\n• Refresh the page\n• Clear browser cache\n• Use an updated browser\n\nIf it still doesn\'t work, contact our support team.'
    },
    {
      category: 'corporate',
      question: 'Do you offer corporate training programs?',
      answer: 'Yes. We provide customized corporate training for technical skills, soft skills, digital skills, and job role–based modules.'
    },
    {
      category: 'corporate',
      question: 'How does Skill Serve support corporate onboarding?',
      answer: 'We assist with:\n• Requirement analysis\n• Customized training design\n• Employee LMS setup\n• Branded dashboards\n• Progress tracking\n• Completion certificates'
    },
    {
      category: 'corporate',
      question: 'Can companies track employee learning progress?',
      answer: 'Yes. Corporate teams get a dedicated dashboard for tracking progress, assessments, completion rate, and reports.'
    },
    {
      category: 'contact',
      question: 'How can I contact Skill Serve support?',
      answer: 'You can reach us through:\n• Email: support@skillserve.in\n• Website Contact Form\n• Phone/WhatsApp (as listed on our Contact page)\n\nWe usually respond within 24 hours.'
    },
  ];

  const filteredFAQs = faqData.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Styles
  const pageWrapStyle = {
    width: '100%',
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
  };

  const heroStyle = {
    background: 'linear-gradient(135deg, #0b3161 0%, #1e4a7a 100%)',
    padding: isDesktop ? '80px 24px' : isMobile ? '60px 20px' : '70px 24px',
    textAlign: 'center',
  };

  const heroTitleStyle = {
    fontFamily: "'Roboto Slab', serif",
    fontSize: isDesktop ? '3rem' : isMobile ? '2rem' : '2.5rem',
    fontWeight: 700,
    color: '#ffffff',
    margin: '0 0 16px 0',
  };

  const heroDescStyle = {
    fontSize: '1.1rem',
    color: 'rgba(255, 255, 255, 0.9)',
    maxWidth: '600px',
    margin: '0 auto',
  };

  const searchContainerStyle = {
    maxWidth: '600px',
    margin: '32px auto 0',
    position: 'relative',
  };

  const searchInputStyle = {
    width: '100%',
    padding: '16px 50px 16px 20px',
    fontSize: '1rem',
    border: 'none',
    borderRadius: '12px',
    outline: 'none',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
  };

  const searchIconStyle = {
    position: 'absolute',
    right: '20px',
    top: '50%',
    transform: 'translateY(-50%)',
  };

  const contentStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: isDesktop ? '60px 24px' : isMobile ? '40px 20px' : '50px 24px',
  };

  const categoriesStyle = {
    display: 'flex',
    gap: '12px',
    marginBottom: '40px',
    overflowX: 'auto',
    paddingBottom: '8px',
  };

  const categoryBtnStyle = (isActive) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    borderRadius: '999px',
    border: 'none',
    background: isActive ? 'linear-gradient(135deg, #0b3161 0%, #1e4a7a 100%)' : '#ffffff',
    color: isActive ? '#ffffff' : '#64748b',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
    boxShadow: isActive ? '0 4px 12px rgba(11, 49, 97, 0.2)' : '0 2px 8px rgba(11, 49, 97, 0.06)',
  });

  const faqListStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const faqItemStyle = {
    background: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(11, 49, 97, 0.04)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  };

  const questionButtonStyle = (isOpen) => ({
    width: '100%',
    padding: isDesktop ? '24px' : '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    textAlign: 'left',
  });

  const questionTextStyle = {
    fontSize: isDesktop ? '1.1rem' : '1rem',
    fontWeight: 700,
    color: '#0b3161',
    flex: 1,
  };

  const chevronStyle = (isOpen) => ({
    flexShrink: 0,
    transition: 'transform 0.3s ease',
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
  });

  const answerStyle = (isOpen) => ({
    maxHeight: isOpen ? '500px' : '0',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
  });

  const answerContentStyle = {
    padding: isDesktop ? '0 24px 24px' : '0 18px 18px',
    fontSize: '1rem',
    lineHeight: 1.8,
    color: '#475569',
    whiteSpace: 'pre-line',
  };

  const noResultsStyle = {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#64748b',
  };

  const ctaBoxStyle = {
    marginTop: '60px',
    padding: isDesktop ? '40px' : '32px',
    background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    borderRadius: '16px',
    textAlign: 'center',
    border: '1px solid #bae6fd',
  };

  const ctaTitleStyle = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#0b3161',
    marginBottom: '12px',
  };

  const ctaDescStyle = {
    fontSize: '1rem',
    color: '#475569',
    marginBottom: '24px',
  };

  const ctaButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 28px',
    background: 'linear-gradient(135deg, #0b3161 0%, #1e4a7a 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '10px',
    fontSize: '1rem',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 20px rgba(11, 49, 97, 0.2)',
  };

  return (
    <><Layout>   <div style={pageWrapStyle}>
      {/* Hero Section */}
      <div style={heroStyle}>
        <h1 style={heroTitleStyle}>Frequently Asked Questions</h1>
        <p style={heroDescStyle}>
          Find answers to common questions about courses, enrollment, payments, and more.
        </p>

        {/* Search Bar */}
        <div style={searchContainerStyle}>
          <input
            type="text"
            placeholder="Search for answers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={searchInputStyle}
          />
          <div style={searchIconStyle}>
            <Search size={20} color="#64748b" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {/* Category Filters */}
        <div style={categoriesStyle}>
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                style={categoryBtnStyle(activeCategory === cat.id)}
                onClick={() => setActiveCategory(cat.id)}
              >
                <Icon size={18} />
                {cat.name}
              </button>
            );
          })}
        </div>

        {/* FAQ List */}
        {filteredFAQs.length > 0 ? (
          <div style={faqListStyle}>
            {filteredFAQs.map((faq, index) => {
              const isOpen = openQuestion === index;
              return (
                <div key={index} style={faqItemStyle}>
                  <button
                    style={questionButtonStyle(isOpen)}
                    onClick={() => setOpenQuestion(isOpen ? null : index)}
                  >
                    <span style={questionTextStyle}>{faq.question}</span>
                    <div style={chevronStyle(isOpen)}>
                      <ChevronDown size={24} color="#0b3161" />
                    </div>
                  </button>
                  <div style={answerStyle(isOpen)}>
                    <div style={answerContentStyle}>{faq.answer}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={noResultsStyle}>
            <HelpCircle size={48} color="#cbd5e1" style={{ marginBottom: '16px' }} />
            <p style={{ fontSize: '1.1rem', fontWeight: 600 }}>No results found</p>
            <p>Try adjusting your search or browse by category</p>
          </div>
        )}

        {/* CTA Box */}
        <div style={ctaBoxStyle}>
          <h3 style={ctaTitleStyle}>Still have questions?</h3>
          <p style={ctaDescStyle}>
            Our support team is here to help you. Get in touch and we'll respond within 24 hours.
          </p>
          <button
            style={ctaButtonStyle}
            onClick={() => window.location.href = '/contact-us'}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 12px 28px rgba(11, 49, 97, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 8px 20px rgba(11, 49, 97, 0.2)';
            }}
          >
            <Mail size={20} />
            Contact Support
          </button>
        </div>
      </div>
    </div>
    </Layout></>
  );
}